import React from "react";
import { makeStyles } from "tss-react/mui";
import { MiIcon } from "@miplatform/components";
import { FONT_WEIGHTS, TYPOGRAPHY_VARIANTS } from "@miplatform/constants";
import { mdiChevronDown } from "@mdi/js";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
  useTheme,
} from "@mui/material";

export const OrderAccordion: React.FC<OrderAccordionProps> = ({
  children,
  title,
  backgroundColor,
  count,
}) => {
  const theme = useTheme();
  const { classes } = useOrderAccordionStyles({
    backgroundColor: backgroundColor ?? theme.palette.grey[500],
  });
  return (
    <Accordion classes={{ root: classes.accordionRoot }} defaultExpanded>
      <AccordionSummary
        classes={{
          root: classes.summaryRoot,
          content: classes.summaryContent,
          expanded: classes.summaryExpanded,
        }}
        expandIcon={
          <MiIcon path={mdiChevronDown} color={theme.palette.common.white} />
        }
      >
        <Typography variant={TYPOGRAPHY_VARIANTS.BD2} className={classes.count}>
          {count}
        </Typography>
        <Typography
          variant={TYPOGRAPHY_VARIANTS.BD2}
          fontWeight={FONT_WEIGHTS.BOLD}
        >
          {title}
        </Typography>
      </AccordionSummary>
      <AccordionDetails classes={{ root: classes.detailsRoot }}>
        {children}
      </AccordionDetails>
    </Accordion>
  );
};

const useOrderAccordionStyles = makeStyles<{ backgroundColor: string }>()(
  (theme, { backgroundColor }) => ({
    accordionRoot: {
      backgroundColor: "transparent",
      boxShadow: "none",
      borderRadius: 0,
      "&:before": { display: "none" },
    },
    summaryRoot: {
      color: theme.palette.common.white,
      backgroundColor: backgroundColor ?? theme.palette.grey[500],
      fontSize: "0.75rem",
      fontWeight: 600,
      minHeight: theme.spacing(4),
      borderRadius: 4,
      border: `1px solid rgba(0,0,0, 0.2)`,
      "&.Mui-expanded": {
        minHeight: theme.spacing(4),
      },
    },
    summaryExpanded: {
      minHeight: theme.spacing(4),
    },
    summaryContent: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: 0,
      margin: 0,
      "&.Mui-expanded": {
        margin: 0,
      },
    },
    count: {
      backgroundColor: "rgba(0, 0, 0, 0.2)",
      color: theme.palette.common.white,
      borderRadius: "100%",
      minWidth: 20,
      height: 20,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    detailsRoot: {
      padding: theme.spacing(1, 0),
      backgroundColor: "transparent",
    },
  })
);

interface OrderAccordionProps {
  children: React.ReactNode;
  title: string;
  backgroundColor?: string;
  count: number;
}
